import { createGlobalStyle } from 'styled-components'

const globalStyle = createGlobalStyle`
  :root {
    /** Base Colors */
    --color-white: #FFFFFF;
    --color-black: #000000;
    --color-gray-050: #F7F7F7;
    --color-gray-100: #F2F2F2;
    --color-gray-200: #E2E2E3;
    --color-gray-300: #C7C8CA;
    --color-gray-400: #AEB0B2;
    --color-gray-500: #939598;
    --color-gray-600: #646568;
    --color-gray-700: #4D4E51;
    --color-gray-800: #3B3C3E;
    --color-gray-900: #252527;
    --color-blue-050: #E6F7FF;
    --color-blue-100: #CBEEFE;
    --color-blue-200: #98D8FE;
    --color-blue-300: #65BDFE;
    --color-blue-400: #05A6FF;
    --color-blue-500: #185CFB;
    --color-blue-600: #0050D1;
    --color-blue-700: #0045B6;
    --color-blue-900: #002279;
    --color-green-050: #EDFDE7;
    --color-green-100: #D7FBC9;
    --color-green-200: #A8F794;
    --color-green-300: #6CE95D;
    --color-green-600: #009D19;
    --color-green-700: #008321;
    --color-green-800: #006A24;
    --color-green-900: #005725;
    --color-yellow-050: #FEFBE1;
    --color-yellow-200: #FFF386;
    --color-yellow-300: #FFE543;
    --color-yellow-400: #FFD006;
    --color-yellow-500: #EFB905;
    --color-yellow-600: #CE9E00;
    --color-yellow-800: #88630B;
    --color-yellow-900: #735611;
    --color-red-050: #FFEDE5;
    --color-red-200: #FFB09D;
    --color-red-400: #FF4A48;
    --color-red-500: #FF0C1F;
    --color-red-600: #DB082C;
    --color-red-700: #B70634;
    --color-red-900: #7A0237;
    --color-purple-200: #E098FE;
    --color-purple-300: #C865FC;
    --color-purple-600: #E098FE;
    --color-purple-800: #37008F;
    --color-teal-300: #62F5D2;
    --color-teal-200: #97FBDB;
    --color-teal-800: #006681;
    --color-orange-200: #FFD19B;
    --color-orange-300: #FFB169;
    --color-orange-800: #931B01;

    /** Alpha Colors */
    --color-black-a04: #000000A3;
    --color-black-a12: #0000001F;
    --color-black-a32: #00000052;
    --color-black-a48: #0000007A;
    --color-black-a64: #FFFFFFA3;
    --color-black-a80: #000000CC;
    --color-white-a04: #FFFFFF0A;
    --color-white-a12: #FFFFFF1F;
    --color-white-a32: #FFFFFF52;
    --color-white-a48: #FFFFFF7A;
    --color-white-a64: #FFFFFFA3;
    --color-white-a80: #FFFFFFCC;

    /** Shadow Helper Tokens */
    --blur-100: 8px;
    --color-luminosity-moderate: rgba(0, 0, 0, 0.12);

    /** Background & Surface Tokens */
    --background-primary: var(--color-white);
    --background-secondary: var(--color-gray-050);

    --surface-neutral-primary: var(--color-white);
    --surface-neutral-secondary: var(--color-gray-050);
    --surface-accent: var(--color-blue-050);
    --surface-success: var(--color-green-050);
    --surface-warning: var(--color-yellow-050);
    --surface-critical: var(--color-red-050);
    --surface-inverse: var(--color-gray-500);

    /** Fill Tokens */
    --fill-neutral-default: var(--color-gray-050);
    --fill-neutral-hover: var(--color-gray-100);
    --fill-neutral-selected: var(--color-gray-300);

    --fill-accent-primary-default: var(--color-blue-500);
    --fill-accent-primary-hover: var(--color-blue-600);
    --fill-accent-primary-selected: var(--color-blue-700);

    --fill-accent-secondary-default: var(--color-blue-050);
    --fill-accent-secondary-hover: var(--color-blue-100);
    --fill-accent-secondary-selected: var(--color-blue-200);

    --fill-success-default: var(--color-green-700);
    --fill-success-hover: var(--color-green-800);
    --fill-success-selected: var(--color-green-900);

    --fill-warning-default: var(--color-yellow-400);
    --fill-warning-hover: var(--color-yellow-500);
    --fill-warning-selected: var(--color-yellow-600);

    --fill-critical-default: var(--color-red-600);
    --fill-critical-hover: var(--color-red-700);
    --fill-critical-selected: var(--color-red-700);

    --fill-decorative-01: var(--color-purple-300);
    --fill-decorative-02: var(--color-teal-300);
    --fill-decorative-03: var(--color-green-300);
    --fill-decorative-04: var(--color-yellow-300);
    --fill-decorative-05: var(--color-orange-300);

    --fill-inverse-default: var(--color-gray-900);
    --fill-inverse-hover: var(--color-gray-800);
    --fill-inverse-selected: var(--color-gray-700);

    --fill-disabled: var(--color-gray-600);

    /** Overlay Tokens */
    --overlay-backdrop: var(--color-black-a64);
    --overlay-image-black: var(--color-black-a48);
    --overlay-image-white: var(--color-white-a64);
    --overlay-luminosity-negative-1: var(--color-black-a04);
    --overlay-luminosity-negative-2: var(--color-black-a12);
    --overlay-luminosity-negative-3: var(--color-black-a32);
    --overlay-luminosity-positive-1: var(--color-white-a32);
    --overlay-luminosity-positive-2: var(--color-white-a48);
    --overlay-luminosity-positive-3: var(--color-white-a80);

    /** Content Tokens */
    --content-neutral-primary: var(--color-gray-900);
    --content-neutral-secondary: var(--color-gray-600);
    --content-neutral-tertiary: var(--color-gray-500);
    --content-accent: var(--fill-accent-primary-default);
    --content-success: var(--color-green-600);
    --content-warning: var(--color-yellow-800);
    --content-critical: var(--color-red-600);
    --content-decorative-01: var(--color-purple-800);
    --content-decorative-02: var(--color-teal-800);
    --content-decorative-03: var(--color-green-800);
    --content-decorative-04: var(--color-yellow-800);
    --content-decorative-05: var(--color-orange-800);
    --content-inverse: var(--color-white);
    --content-disabled: var(--color-gray-600);

    /** Link Tokens */
    --link-default: var(--fill-accent-primary-default);
    --link-hover: var(--color-blue-700);
    --link-visited: var(--color-purple-600);
    --link-inverse-default: var(--color-white);
    --link-inverse-hover: var(--color-gray-200);
    --link-inverse-visited: var(--color-purple-200);

    /** Border Tokens */
    --border-neutral-default: var(--color-gray-200);
    --border-neutral-hover: var(--color-gray-300);
    --border-neutral-selected: var(--color-gray-400);

    --border-accent-default: var(--color-blue-300);
    --border-accent-hover: var(--color-blue-400);
    --border-accent-selected: var(--color-blue-500);

    --border-success: var(--color-green-700);
    --border-warning: var(--color-yellow-700);
    --border-critical: var(--color-red-700);

    --border-focus-default: var(--color-blue-300);
    --border-focus-inverse: var(--color-white);

    --border-inverse-default: var(--color-gray-600);
    --border-inverse-hover: var(--color-gray-500);
    --border-inverse-selected: var(--color-gray-400);

    /** Typography Tokens */
    --font-family-sans: "Inter", sans-serif;
    --font-family-display-01: var(--font-family-sans);
    --font-weight-regular: 400;
    --font-weight-medium: 500;
    --font-weight-semibold: 600;
    --font-size-0625: 0.625rem;
    --font-size-075: 0.75rem;
    --font-size-087: 0.875rem;
    --font-size-100: 1rem;
    --font-size-125: 1.25rem;
    --font-size-150: 1.5rem;
    --font-size-200: 2rem;
    --font-size-250: 2.5rem;
    --font-size-300: 3rem;
    --font-size-350: 3.5rem;
    --font-size-425: 4.25rem;
    --font-size-500: 5rem;
    --font-line-height-100: 1;
    --font-line-height-125: 1.25;
    --font-line-height-150: 1.5;
    --font-line-height-200: 2;
    --font-line-height-175: 1.75;
    --font-line-height-250: 2.5;
    --font-line-height-300: 3;
    --font-line-height-350: 3.5;
    --font-line-height-425: 4.25;
    --font-line-height-500: 5;

    /** Typography Color Fill Tokens */
    --color-fill-primary: var(--content-neutral-primary);
    --color-fill-secondary: var(--content-neutral-secondary);
    --color-fill-tertiary: var(--content-neutral-tertiary);
    --color-fill-accent: var(--content-accent);
    --color-fill-success: var(--content-success);
    --color-fill-warning: var(--content-warning);
    --color-fill-critical: var(--content-critical);
    --color-fill-inverse: var(--content-inverse);
  }

  /** Dark Theme Overrides */
  html[data-theme='dark'] {
    --background-primary: var(--color-black);
    --background-secondary: var(--color-gray-900);

    --surface-neutral-primary: var(--color-black);
    --surface-neutral-secondary: var(--color-gray-900);
    --surface-accent: var(--color-blue-900);
    --surface-success: var(--color-green-900);
    --surface-warning: var(--color-yellow-900);
    --surface-critical: var(--color-red-900);
    --surface-inverse: var(--color-gray-300);

    /** Fill Tokens (Dark) */
    --fill-neutral-default: var(--color-gray-900);
    --fill-neutral-hover: var(--color-gray-800);
    --fill-neutral-selected: var(--color-gray-700);

    --fill-accent-primary-default: var(--color-blue-200);
    --fill-accent-primary-hover: var(--color-blue-300);
    --fill-accent-primary-selected: var(--color-blue-400);

    --fill-accent-secondary-default: var(--color-blue-900);
    --fill-accent-secondary-hover: var(--color-blue-800);
    --fill-accent-secondary-selected: var(--color-blue-700);

    --fill-success-default: var(--color-green-300);
    --fill-success-hover: var(--color-green-200);
    --fill-success-selected: var(--color-green-100);

    --fill-warning-default: var(--color-yellow-400);
    --fill-warning-hover: var(--color-yellow-300);
    --fill-warning-selected: var(--color-yellow-200);

    --fill-critical-default: var(--color-red-600);
    --fill-critical-hover: var(--color-red-500);
    --fill-critical-selected: var(--color-red-400);

    --fill-decorative-01: var(--color-purple-300);
    --fill-decorative-02: var(--color-teal-300);
    --fill-decorative-03: var(--color-green-300);
    --fill-decorative-04: var(--color-yellow-300);
    --fill-decorative-05: var(--color-orange-300);

    --fill-inverse-default: var(--color-gray-050);
    --fill-inverse-hover: var(--color-gray-100);
    --fill-inverse-selected: var(--color-gray-200);

    --fill-disabled: var(--color-gray-400);

    /** Overlay Tokens (Dark) */
    --overlay-backdrop: var(--color-black-a64);
    --overlay-image-black: var(--color-black-a48);
    --overlay-image-white: var(--color-white-a64);
    --overlay-luminosity-negative-1: var(--color-black-a32);
    --overlay-luminosity-negative-2: var(--color-black-a48);
    --overlay-luminosity-negative-3: var(--color-black-a80);
    --overlay-luminosity-positive-1: var(--color-white-a04);
    --overlay-luminosity-positive-2: var(--color-white-a12);
    --overlay-luminosity-positive-3: var(--color-white-a32);

    /** Content Tokens (Dark) */
    --content-neutral-primary: var(--color-gray-050);
    --content-neutral-secondary: var(--color-gray-300);
    --content-neutral-tertiary: var(--color-gray-500);
    --content-accent: var(--color-blue-200);
    --content-success: var(--color-green-200);
    --content-warning: var(--color-yellow-200);
    --content-critical: var(--color-red-200);
    --content-decorative-01: var(--color-purple-200);
    --content-decorative-02: var(--color-teal-200);
    --content-decorative-03: var(--color-green-200);
    --content-decorative-04: var(--color-yellow-200);
    --content-decorative-05: var(--color-orange-200);
    --content-inverse: var(--color-black);
    --content-disabled: var(--color-gray-400);

    /** Link Tokens (Dark) */
    --link-default: var(--color-blue-200);
    --link-hover: var(--color-blue-300);
    --link-visited: var(--color-purple-200);
    --link-inverse-default: var(--color-black);
    --link-inverse-hover: var(--color-gray-700);
    --link-inverse-visited: var(--color-purple-600);

    /** Border Tokens (Dark) */
    --border-neutral-default: var(--color-gray-700);
    --border-neutral-hover: var(--color-gray-600);
    --border-neutral-selected: var(--color-gray-500);

    --border-accent-default: var(--color-blue-700);
    --border-accent-hover: var(--color-blue-600);
    --border-accent-selected: var(--color-blue-500);

    --border-success: var(--color-green-200);
    --border-warning: var(--color-yellow-200);
    --border-critical: var(--color-red-200);

    --border-focus-default: var(--color-blue-300);
    --border-focus-inverse: var(--color-black);

    --border-inverse-default: var(--color-gray-500);
    --border-inverse-hover: var(--color-gray-400);
    --border-inverse-selected: var(--color-gray-300);

    /** Typography Color Fill Tokens (Dark) */
    --color-fill-primary: var(--content-neutral-primary);
    --color-fill-secondary: var(--content-neutral-secondary);
    --color-fill-tertiary: var(--content-neutral-tertiary);
    --color-fill-accent: var(--content-accent);
    --color-fill-success: var(--content-success);
    --color-fill-warning: var(--content-warning);
    --color-fill-critical: var(--content-critical);
    --color-fill-inverse: var(--content-inverse);
  }

  * {
    box-sizing: border-box;
    font-family: var(--font-family-sans) !important;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      @supports (contain: paint) {
        scrollbar-width: thin;
        ::-webkit-scrollbar {
          width: 8px;
          height: 4px;
        }
        ::-webkit-scrollbar-button {
          display: none;
        }
        ::-webkit-scrollbar-thumb {
          background-color: #00000000;
          box-shadow: 180px 0px 0px 180px #00000040 inset;
          border-radius: ${({ theme }) => theme.shape.borderRadius}px;
        }
      }
    }
  }

  html {
    font-size: 16px;
    overflow-y: overlay;
  }

  body {
    margin: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    overflow: hidden;
    & > div {
      overflow-y: overlay;
    }
  }
`

export default globalStyle
